import { cn } from "@/lib/utils";

interface LogotypeProps {
  variant?: "one" | "pro";
  size?: "sm" | "md" | "lg" | "xl";
  glow?: boolean;
  className?: string;
}

const SIZES = {
  sm: "text-lg",
  md: "text-2xl",
  lg: "text-4xl",
  xl: "text-6xl",
} as const;

export function Logotype({
  size = "md",
  glow = false,
  className,
}: LogotypeProps) {
  return (
    <span
      style={{
        fontFamily: "var(--font-stix-two-text), 'Cambria Math', Georgia, serif",
      }}
      className={cn(
        "inline-flex items-baseline font-light tracking-[0.06em] italic",
        SIZES[size],
        glow && "text-glow-interactive",
        className,
      )}
    >
      <span>αβΓ</span>
      <sup
        className="ml-[0.25em] text-[0.6em] leading-none not-italic"
        style={{ verticalAlign: "super" }}
      >
        ✻
      </sup>
      <span className="ml-[0.1em] not-italic">1</span>
    </span>
  );
}

const LOGOTYPE_TOKEN_RE = /(\{\{abg\}\}|\{\{abg-pro\}\})/g;

/**
 * Renders a plain string with current or legacy ABG tokens replaced by the
 * unified <Logotype />. The `abg-pro` token remains readable for old content
 * but no longer produces a different customer-facing brand.
 */
export function LogotypeText({
  children,
  size = "sm",
  className,
}: {
  children: string;
  size?: LogotypeProps["size"];
  className?: string;
}) {
  const parts = children.split(LOGOTYPE_TOKEN_RE);
  return (
    <>
      {parts.map((part, i) => {
        if (part === "{{abg}}")
          return (
            <Logotype key={i} className={className} size={size} variant="one" />
          );
        if (part === "{{abg-pro}}")
          return (
            <Logotype key={i} className={className} size={size} variant="one" />
          );
        return part;
      })}
    </>
  );
}
